import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { showData } from "../../Utils/APIs/commonShowAPI";

const API_PATH = "installer/selectionPaymentRequest";

const formatDate = (value) => {
  if (!value) return "N/A";
  const d = new Date(value);
  return isNaN(d) ? "N/A" : d.toISOString().split("T")[0];
};

const formatAmount = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value || 0);

const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";

const maskAccount = (num = "") =>
  num.length > 4 ? "•".repeat(num.length - 4) + num.slice(-4) : num;

// installerId is a populated object; fall back if it is a plain string
const getInstaller = (item) =>
  typeof item.installerId === "object" && item.installerId !== null
    ? item.installerId
    : { _id: item.installerId, name: item.installerId || "N/A" };

/* ---------- small icons ---------- */
const Chevron = ({ open }) => (
  <svg
    className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" />
  </svg>
);

const EyeIcon = ({ off }) => (
  <svg
    className="h-4 w-4"
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
  >
    <path d="M1.5 10S4.5 4.5 10 4.5 18.5 10 18.5 10 15.5 15.5 10 15.5 1.5 10 1.5 10z" />
    <circle cx="10" cy="10" r="2.5" />
    {off && <path d="M3 17L17 3" />}
  </svg>
);

const CopyIcon = ({ done }) =>
  done ? (
    <svg
      className="h-4 w-4 text-green-600"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M4 10.5l4 4 8-9" />
    </svg>
  ) : (
    <svg
      className="h-4 w-4"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
    >
      <rect x="7" y="7" width="10" height="10" rx="2" />
      <path d="M13 7V5a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2" />
    </svg>
  );

const IconButton = ({ onClick, label, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
  >
    {children}
  </button>
);

/* A label/value pair used in the bank details grid */
const Field = ({ label, children, actions }) => (
  <div className="min-w-0">
    <dt className="text-xs text-gray-500">{label}</dt>
    <dd className="mt-0.5 flex items-center gap-1 text-sm font-medium text-gray-900">
      <span className="truncate">{children || "N/A"}</span>
      {actions}
    </dd>
  </div>
);

const SpList = () => {
  const [spList, setSpList] = useState([]);
  const [loadingGroup, setLoadingGroup] = useState(null);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({}); // key -> bool (account details open)
  const [revealed, setRevealed] = useState({}); // key -> bool (account no. visible)
  const [selection, setSelection] = useState({}); // key -> selected request ids
  const [copied, setCopied] = useState(null);

  const fetchList = async () => {
    await showData(API_PATH, setSpList);
    setSelection({});
  };

  useEffect(() => {
    fetchList();
  }, []);

  // Group by installer _id
  const groups = useMemo(() => {
    const map = new Map();
    spList.forEach((item) => {
      const inst = getInstaller(item);
      const key = inst._id || inst.name;
      if (!map.has(key))
        map.set(key, { key, installer: inst, ids: [], items: [] });
      const g = map.get(key);
      g.ids.push(item.id);
      g.items.push(item);
    });
    return Array.from(map.values());
  }, [spList]);

  const visibleGroups = groups.filter((g) =>
    (g.installer.name || "")
      .toLowerCase()
      .includes(search.trim().toLowerCase()),
  );

  const selectedOf = (g) => selection[g.key] ?? g.ids; // default: all selected

  const toggleRequest = (g, id) => {
    const current = selectedOf(g);
    const next = current.includes(id)
      ? current.filter((x) => x !== id)
      : [...current, id];
    setSelection((prev) => ({ ...prev, [g.key]: next }));
  };

  const toggleAllInGroup = (g) => {
    const all = selectedOf(g).length === g.ids.length;
    setSelection((prev) => ({ ...prev, [g.key]: all ? [] : g.ids }));
  };

  const copyText = async (text, tag) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(tag);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      alert("Copy failed");
    }
  };

  const updateGroup = async (g, status) => {
    const ids = selectedOf(g);
    const label = status ? "Approve" : "Reject";
    if (
      !window.confirm(
        `${label} ${ids.length} request(s) for ${g.installer.name}?`,
      )
    )
      return;

    try {
      setLoadingGroup(g.key);
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/${API_PATH}`,
        { ids, status },
      );
      if (res.data?.success) {
        await fetchList();
        alert(res.data.message);
      }
    } catch (error) {
      alert(error?.response?.data?.message || "Something went wrong");
    } finally {
      setLoadingGroup(null);
    }
  };

  const grandTotal = spList.reduce(
    (sum, i) => sum + (Number(i.amount) || 0),
    0,
  );

  return (
    <div className="w-full h-full flex flex-col">
      <div className="mainHeading">
        <h1 className="text-3xl font-semibold tracking-widest text-center p-2 bg-gray-50 rounded-lg">
          S<span className="text-lg">P-PAYMENT</span> L
          <span className="text-lg">IST</span>
        </h1>
      </div>

      {/* Toolbar: summary + search */}
      <div className="flex flex-wrap items-center gap-3 px-4 py-4">
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-2">
          <p className="text-xs text-gray-500">Installers</p>
          <p className="text-lg font-semibold text-gray-900">{groups.length}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-2">
          <p className="text-xs text-gray-500">Requests</p>
          <p className="text-lg font-semibold text-gray-900">{spList.length}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-2">
          <p className="text-xs text-gray-500">Pending amount</p>
          <p className="text-lg font-semibold text-green-700">
            {formatAmount(grandTotal)}
          </p>
        </div>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search installer"
          className="ml-auto w-full sm:w-72 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-gray-800 focus:ring-1 focus:ring-gray-800"
        />
      </div>

      <div className="flex-1 min-h-0 overflow-auto px-4 pb-6">
        {visibleGroups.length === 0 ? (
          <p className="py-10 text-center text-gray-500">No pending requests</p>
        ) : (
          <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
            {visibleGroups.map((g) => {
              const inst = g.installer;
              const busy = loadingGroup === g.key;
              const isOpen = !!expanded[g.key]; // account details folded by default
              const isRevealed = !!revealed[g.key];
              const selected = selectedOf(g);
              const allChecked = selected.length === g.ids.length;
              const selectedTotal = g.items
                .filter((i) => selected.includes(i.id))
                .reduce((s, i) => s + (Number(i.amount) || 0), 0);
              const requesters =
                [...new Set(g.items.map((i) => i.createdBy))]
                  .filter(Boolean)
                  .join(", ") || "N/A";

              return (
                <section
                  key={g.key}
                  className="flex h-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
                >
                  {/* Header */}
                  <div className="flex w-full items-center gap-4 px-5 py-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-gray-800 text-sm font-semibold text-white">
                      {initials(inst.name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="truncate text-base font-semibold text-gray-900">
                        {inst.name}
                      </h2>
                      <p className="truncate text-sm text-gray-500">
                        {[inst.district, inst.state]
                          .filter(Boolean)
                          .join(", ") || "—"}
                      </p>
                    </div>
                    <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                      {g.items.length}{" "}
                      {g.items.length === 1 ? "request" : "requests"}
                    </span>
                  </div>

                  {/* Summary */}
                  <div className="grid grid-cols-2 border-y border-gray-200 bg-gray-50">
                    <div className="px-5 py-3">
                      <p className="text-xs text-gray-500">Selected requests</p>
                      <p className="text-xl font-semibold text-gray-900">
                        {selected.length}
                        <span className="text-sm font-normal text-gray-500">
                          {" "}
                          of {g.items.length}
                        </span>
                      </p>
                    </div>
                    <div className="border-l border-gray-200 px-5 py-3">
                      <p className="text-xs text-gray-500">Payable amount</p>
                      <p className="text-xl font-semibold text-green-700">
                        {formatAmount(selectedTotal)}
                      </p>
                    </div>
                  </div>

                  {/* Account details (folded by default) */}
                  <div className="border-b border-gray-200">
                    <button
                      type="button"
                      onClick={() =>
                        setExpanded((p) => ({ ...p, [g.key]: !p[g.key] }))
                      }
                      aria-expanded={isOpen}
                      className="flex w-full items-center justify-between px-5 py-3 text-left hover:bg-gray-50"
                    >
                      <span className="text-sm font-semibold text-gray-900">
                        Account details
                      </span>
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        {isOpen ? "Hide" : "Show"}
                        <Chevron open={isOpen} />
                      </span>
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-4">
                        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                          <Field label="Account holder">
                            {inst.accountHolderName}
                          </Field>
                          <Field label="Bank">{inst.bankName}</Field>
                          <Field
                            label="Account number"
                            actions={
                              inst.accountNumber && (
                                <>
                                  <IconButton
                                    label={
                                      isRevealed
                                        ? "Hide account number"
                                        : "Show account number"
                                    }
                                    onClick={() =>
                                      setRevealed((p) => ({
                                        ...p,
                                        [g.key]: !p[g.key],
                                      }))
                                    }
                                  >
                                    <EyeIcon off={isRevealed} />
                                  </IconButton>
                                  <IconButton
                                    label="Copy account number"
                                    onClick={() =>
                                      copyText(
                                        inst.accountNumber,
                                        `${g.key}-acc`,
                                      )
                                    }
                                  >
                                    <CopyIcon
                                      done={copied === `${g.key}-acc`}
                                    />
                                  </IconButton>
                                </>
                              )
                            }
                          >
                            <span className="font-mono">
                              {isRevealed
                                ? inst.accountNumber
                                : maskAccount(inst.accountNumber)}
                            </span>
                          </Field>
                          <Field
                            label="IFSC code"
                            actions={
                              inst.IFSC_code && (
                                <IconButton
                                  label="Copy IFSC code"
                                  onClick={() =>
                                    copyText(inst.IFSC_code, `${g.key}-ifsc`)
                                  }
                                >
                                  <CopyIcon done={copied === `${g.key}-ifsc`} />
                                </IconButton>
                              )
                            }
                          >
                            <span className="font-mono">{inst.IFSC_code}</span>
                          </Field>
                          <Field
                            label="Contact"
                            actions={
                              inst.contact && (
                                <IconButton
                                  label="Copy contact number"
                                  onClick={() =>
                                    copyText(inst.contact, `${g.key}-tel`)
                                  }
                                >
                                  <CopyIcon done={copied === `${g.key}-tel`} />
                                </IconButton>
                              )
                            }
                          >
                            {inst.contact}
                          </Field>
                        </dl>
                      </div>
                    )}
                  </div>

                  {/* Requests table (always visible) */}
                  <div className="flex-1">
                    <div
                      className="overflow-auto"
                      style={{ minHeight: "12rem", maxHeight: "18rem" }}
                    >
                      <table className="w-full border-collapse text-sm">
                        <thead className="sticky top-0 bg-gray-800 text-xs uppercase text-gray-100">
                          <tr>
                            <th className="w-12 px-5 py-2.5 text-left">
                              <input
                                type="checkbox"
                                checked={allChecked}
                                onChange={() => toggleAllInGroup(g)}
                                aria-label="Select all requests"
                              />
                            </th>
                            <th className="px-2 py-2.5 text-left font-semibold">
                              Saral-Id
                            </th>
                            <th className="px-2 py-2.5 text-left font-semibold">
                              Requested
                            </th>
                            <th className="px-5 py-2.5 text-right font-semibold">
                              Amount
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.items.map((item) => {
                            const checked = selected.includes(item.id);
                            return (
                              <tr
                                key={item.id}
                                onClick={() => toggleRequest(g, item.id)}
                                className={`cursor-pointer border-b border-gray-200 transition-colors ${
                                  checked ? "bg-blue-50" : "hover:bg-gray-50"
                                }`}
                              >
                                <td className="px-5 py-2.5">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleRequest(g, item.id)}
                                    onClick={(e) => e.stopPropagation()}
                                    aria-label={`Select ${item.saralId}`}
                                  />
                                </td>
                                <td className="px-2 py-2.5 font-medium text-gray-900">
                                  {item.saralId || "N/A"}
                                </td>
                                <td className="px-2 py-2.5 text-gray-600">
                                  {formatDate(item.createdAt)}
                                </td>
                                <td className="px-5 py-2.5 text-right text-gray-900">
                                  {formatAmount(item.amount)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Footer actions */}
                  <div className="flex flex-wrap items-center justify-between gap-3 bg-gray-50 px-5 py-3">
                    <p className="text-xs text-gray-500">
                      Requested by {requesters}
                    </p>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => updateGroup(g, false)}
                        disabled={busy || selected.length === 0}
                        className="rounded-lg border border-red-300 bg-white px-5 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Reject
                      </button>
                      <button
                        type="button"
                        onClick={() => updateGroup(g, true)}
                        disabled={busy || selected.length === 0}
                        className="rounded-lg bg-green-600 px-5 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {busy ? "Please wait..." : "Approve"}
                      </button>
                    </div>
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SpList;